import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FiAlertTriangle, FiCheckCircle, FiEdit3, FiInfo, FiX } from 'react-icons/fi';
import { financeAPI } from '../../services/api';
import * as fs from './financeStyles';

// Guided transaction entry for a CFO without an accounting background —
// see VEC_MS_FINANCE_ENTRY_GUIDE.md, the plain-language companion to this
// page. The CFO never picks a debit/credit side directly: they say whether
// money came IN or went OUT, and the system builds the correct two-line
// entry (finance/quick_entry.py).
const REVENUE_CATEGORIES = [
  ['4000', 'Application Fee Revenue'], ['4010', 'Agent Fee Revenue'], ['4020', 'Itinerary Fee Revenue'],
  ['4030', 'In-Turkey Assistance Revenue'], ['4040', 'Insurance Fee Revenue'],
  ['4050', 'Residence Permit Processing Revenue'], ['4060', 'Airport Pickup Revenue'],
  ['4070', 'Other Service Revenue'], ['2000', 'Due to Director (Tshidiso) — he is repaying the company'],
  ['2100', 'Amounts Owing to Individuals — being repaid'],
];
const EXPENSE_CATEGORIES = [
  ['5000', 'Website & Hosting Expense'], ['5010', 'Marketing Expense'], ['5020', 'Transport Expense'],
  ['5030', 'Student Food/Welfare Expense'], ['5040', 'Wages Expense'], ['5050', 'Communication Expense'],
  ['5060', 'Airport Pickup Expense'], ['5070', 'Insurance Expense'], ['5080', 'Office Rent Expense'],
  ['5090', 'Call Center Expense'], ['5100', 'Employee Assistance Expense'],
  ['5110', 'Money Transfer/Bank Charges Expense'], ['5120', 'Stationery & Supplies Expense'],
  ['5130', 'Company Development Expense'], ['5140', 'Tax & Compliance Expense'],
  ['5150', 'Marketing Materials Expense'], ['5160', 'Referral/Reference Expense'],
  ['5999', 'Uncategorized Expense — not sure? pick this, it gets flagged for review'],
  ['2000', 'Due to Director (Tshidiso) — he is putting money into the company'],
  ['2100', 'Amounts Owing to Individuals — someone else is fronting money'],
];
const CURRENCIES = ['USD', 'TRY', 'ZAR', 'EUR', 'GBP'];

const QuickEntry = () => {
  const [entities, setEntities] = useState([]);
  const [entityCode, setEntityCode] = useState('');
  const [direction, setDirection] = useState('in');
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [description, setDescription] = useState('');
  const [amount, setAmount] = useState('');
  const [currencyCode, setCurrencyCode] = useState('USD');
  const [accountCode, setAccountCode] = useState('');
  const [suggestion, setSuggestion] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    financeAPI.entities.list().then((res) => {
      const list = res.data.results || res.data || [];
      setEntities(list);
      if (list.length && !entityCode) setEntityCode(list[0].code);
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const categoryOptions = direction === 'in' ? REVENUE_CATEGORIES : EXPENSE_CATEGORIES;

  // Debounced category suggestion as the CFO types a description — a
  // suggestion, never applied automatically; they always see and confirm
  // it (VEC_MS_FINANCE_ENTRY_GUIDE.md section 6: a flagged guess costs a
  // few minutes, a silently-trusted wrong one costs much more later).
  useEffect(() => {
    if (!description || description.trim().length < 4) {
      setSuggestion(null);
      return undefined;
    }
    const handle = setTimeout(() => {
      financeAPI.entries.suggestCategory(description, direction)
        .then((res) => setSuggestion(res.data.account_code ? res.data : null))
        .catch(() => setSuggestion(null));
    }, 500);
    return () => clearTimeout(handle);
  }, [description, direction]);

  const applySuggestion = useCallback(() => {
    if (suggestion) setAccountCode(suggestion.account_code);
  }, [suggestion]);

  const suggestionLabel = useMemo(() => {
    if (!suggestion) return null;
    const match = categoryOptions.find(([code]) => code === suggestion.account_code);
    return match ? match[1] : suggestion.account_code;
  }, [suggestion, categoryOptions]);

  const handleDirectionChange = (next) => {
    setDirection(next);
    setAccountCode('');
    setSuggestion(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    setResult(null);
    try {
      const res = await financeAPI.entries.quickEntry({
        entity_code: entityCode, date, description, amount, currency_code: currencyCode,
        direction, account_code: accountCode,
      });
      setResult(res.data);
      setDescription(''); setAmount(''); setAccountCode(''); setSuggestion(null);
    } catch (err) {
      setError(err?.response?.data?.error || 'Could not save this entry.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={fs.page}>
      <div style={fs.pageHeader}>
        <div>
          <h1 style={fs.pageTitle}><FiEdit3 size={20} />Quick Entry</h1>
          <p style={fs.pageSubtitle}>
            Enter money in or out — the system builds the correct accounting entry for you.
            New here? Ask the Chairman for the finance entry guide.
          </p>
        </div>
      </div>

      {error && (
        <div style={fs.errorBanner}>
          <FiAlertTriangle size={18} /><span>{error}</span>
          <button onClick={() => setError(null)} style={{ marginLeft: 'auto', background: 'none', border: 'none', color: fs.colors.red, cursor: 'pointer' }}><FiX size={16} /></button>
        </div>
      )}

      {result && (
        <div style={{ ...fs.card, borderColor: 'rgba(67,233,123,0.35)', background: 'rgba(67,233,123,0.06)', marginBottom: 20, display: 'flex', alignItems: 'center', gap: 12 }}>
          <FiCheckCircle size={20} color={fs.colors.green} />
          <div>
            <div style={{ color: fs.colors.textBright, fontWeight: 600 }}>
              {result.posted ? 'Entry saved and posted.' : 'Entry saved as a draft.'}
            </div>
            {result.note && <div style={{ color: fs.colors.textDim, fontSize: 13, marginTop: 2 }}>{result.note}</div>}
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} style={{ ...fs.card, maxWidth: 640, display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div>
          <label style={fs.label}>Is money coming in, or going out?</label>
          <div style={{ display: 'flex', gap: 10 }}>
            <button type="button" onClick={() => handleDirectionChange('in')}
              style={direction === 'in' ? fs.successButton : fs.neutralButton}>Money in (Revenue)</button>
            <button type="button" onClick={() => handleDirectionChange('out')}
              style={direction === 'out' ? fs.dangerButton : fs.neutralButton}>Money out (Expense)</button>
          </div>
        </div>

        <div>
          <label style={fs.label}>Entity</label>
          <select value={entityCode} onChange={(e) => setEntityCode(e.target.value)} style={{ ...fs.select, width: '100%' }} required>
            {entities.map((ent) => <option key={ent.code} value={ent.code}>{ent.name}</option>)}
          </select>
        </div>

        <div>
          <label style={fs.label}>Date</label>
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} style={{ ...fs.input, width: '100%' }} required />
        </div>

        <div>
          <label style={fs.label}>Description</label>
          <input type="text" value={description} onChange={(e) => setDescription(e.target.value)}
            placeholder="e.g. Agent fee from Kemal, or Monthly hosting subscription"
            style={{ ...fs.input, width: '100%' }} required />
          {suggestion && (
            <div style={{ marginTop: 8, fontSize: 12, color: fs.colors.textDim, display: 'flex', alignItems: 'center', gap: 6 }}>
              <FiInfo size={13} />
              Suggested: <strong>{suggestionLabel}</strong>
              {suggestion.confidence === 'low' && <span style={{ color: fs.colors.amber }}>(low confidence — double-check this one)</span>}
              <button type="button" onClick={applySuggestion} style={{ ...fs.neutralButton, padding: '3px 10px', fontSize: 11 }}>Use this</button>
            </div>
          )}
        </div>

        <div style={{ display: 'flex', gap: 14 }}>
          <div style={{ flex: 1 }}>
            <label style={fs.label}>Amount</label>
            <input type="number" min="0.01" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)}
              style={{ ...fs.input, width: '100%' }} required />
          </div>
          <div style={{ width: 120 }}>
            <label style={fs.label}>Currency</label>
            <select value={currencyCode} onChange={(e) => setCurrencyCode(e.target.value)} style={{ ...fs.select, width: '100%' }}>
              {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
        </div>

        <div>
          <label style={fs.label}>Category</label>
          <select value={accountCode} onChange={(e) => setAccountCode(e.target.value)} style={{ ...fs.select, width: '100%' }} required>
            <option value="">Choose a category…</option>
            {categoryOptions.map(([code, name]) => <option key={code} value={code}>{name}</option>)}
          </select>
        </div>

        <button type="submit" disabled={submitting} style={{ ...fs.primaryButton, justifyContent: 'center' }}>
          {submitting ? 'Saving…' : 'Save entry'}
        </button>
      </form>
    </div>
  );
};

export default QuickEntry;
